import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import {
    AggregationMode,
    ApprovalSettings,
    ComponentDefinition,
    Dictionary,
    Group,
    IApprovalStrategy,
    ISortConfiguration,
    IValueViewModel,
    Identifier,
    IdentifierType,
    SeriesSelectorViewModel
} from '../../../models/index';
import { ISeriesSelectorService, IStoreService } from '../../../services';
import { IRootScope } from '../../../IRootScope';
import { toggleEditMode, tagDefinitionValues, seriesAdded, identifierValues, validatedValues } from '../../../common';
import { StoreElement } from '../../../stateManagement';


interface SeriesModel {
    aggregationMode: AggregationMode;
    guid: string;
    sortingOrder: number;
    table: number;
    value: string;
}

enum ApprovalSerieTable {
    Both = 0,
    Left = 1,
    Right = 2
}

class ApprovalConfigurationStateController implements angular.IComponentController, angular.IOnInit, angular.IOnDestroy {
    approvalStrategy: IApprovalStrategy;
    config: ApprovalSettings;
    formController: angular.IFormController;
    isEditMode = true;
    isLoading: boolean;
    returnParams: any;
    series: any[];
    seriesCollection: { model: SeriesModel, originalSeries: any }[];
    sortConfig: ISortConfiguration = {
        animation: 150,
        handle: '.drag-handle',
        onSort: evt => this.sort(evt)
    };
    tableProperties = [
        { key: ApprovalSerieTable.Both, value: 'Both' },
        { key: ApprovalSerieTable.Left, value: 'Aggregated (left)' },
        { key: ApprovalSerieTable.Right, value: 'Detailed (right)' }
    ];
    viewModels: Dictionary<IValueViewModel>;

    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$rootScope',
        '$stateParams',
        'storeService',
        'seriesSelectorService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$stateParams: angular.ui.IStateParamsService,
        private _storeService: IStoreService,
        private _seriesSelectorService: ISeriesSelectorService
    ) {
    }

    $onInit() {
        this.returnParams = this._$stateParams['returnParams']
            ? this._$stateParams['returnParams']
            : {};

        if (!this._$rootScope.isEditMode) {
            this._$rootScope.$broadcast(toggleEditMode);
        }
        const strategyGuid = this._$stateParams['approvalStrategyGuid'];
        this.seriesCollection = [];
        this.isLoading = true;
        this._unsubscribe = this._storeService.register(store => {
            const { groups, approvalSettings, approvalStrategies, valueViewModels } = store;
            this.config = approvalSettings.dictionary[strategyGuid];
            this.approvalStrategy = approvalStrategies.dictionary[strategyGuid];
            this.viewModels = valueViewModels.dictionary;
            const identifiers: Identifier[] = [].concat(..._.values<Group>(groups.dictionary)
                .filter(g => g.approvalStrategyGuid === strategyGuid)
                .map(g => g.identifierCollection)
            );
            this._seriesSelectorService.getViewModels(identifiers.map(d => d.guid))
                .then(data => {
                    this.series = data;
                    this.seriesCollection = this.config.series.map(d => {
                        if (d.table === undefined) {
                            d.table = 0;
                        }
                        return {
                            model: d,
                            originalSeries: _.find(this.series, s => s.guid === d.guid)
                        };
                    });
                })
                .finally(() => this.isLoading = false);
        }, [StoreElement.ApprovalSettings, StoreElement.ApprovalStrategies, StoreElement.Groups, StoreElement.ValueViewModels]);



        this._$scope.$on(seriesAdded, (event, data) => {
            if (data) {
                this.add(data);
            }
        });
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    addSeries(data) {
        const series = _.find(this.series, s => s.guid === data.itemId);
        if (series) {
            this.add(series);
        }
    }

    remove(series: any) {
        this.seriesCollection.splice(this.seriesCollection.indexOf(series), 1);
        if (this.formController) {
            this.formController.$setDirty();
        }
    }

    save(model: ApprovalSettings) {
        model.guid = this._$stateParams['approvalStrategyGuid'];
        this.isLoading = true;
        return this._storeService.dispatchers()
            .approvalSettings
            .update(model)
            .finally(() => this.isLoading = false);
    }

    hasReconciled(guid: string) {
        const vm = this.viewModels[guid];
        if (vm) {
            return vm.hasReconciledValue;
        } else {
            return false;
        }
    }

    private sort(evt) {
        evt.models.forEach((s: any, i) => s.model.sortingOrder = i);
        this.seriesCollection = angular.copy(evt.models);
    }

    private add(serie: SeriesSelectorViewModel): void {
        this.seriesCollection.push({
            model: {
                guid: serie.guid,
                value: this.getDefaultValue(serie),
                aggregationMode: AggregationMode.Average,
                sortingOrder: d3.max(this.seriesCollection.map(d => d.model), m => m.sortingOrder) + 1,
                table: 0
            },
            originalSeries: serie
        });
        if (this.formController) {
            this.formController.$setDirty();
        }
    }

    private getDefaultValue(serie: SeriesSelectorViewModel): string {
        const identifierType = this.getRealType(serie);

        switch (identifierType) {
            case IdentifierType.Tag:
                return tagDefinitionValues[0].key;
            case IdentifierType.CalculatedValue:
                return identifierValues[0].key;
            case IdentifierType.Validated:
                return validatedValues[0].key;
            default:
                console.log(`Identifier type ${identifierType} not supported`);
                return 'average';
        }
    }

    getRealType(serie: SeriesSelectorViewModel): IdentifierType {
        return serie
            ? serie.identifierType === IdentifierType.Linked
                ? serie.linkedType
                : serie.identifierType
            : IdentifierType.Tag;
    }
}

export const wtApprovalConfigurationState: ComponentDefinition = {
    componentId: 'wtApprovalConfigurationState',
    controller: ApprovalConfigurationStateController,
    template: require('./approvalConfigurationState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};